import { cn } from '../lib/utils'

// Above this many invites the per-invite pip meter stops scaling (a 100-pip row is
// unreadable), so `auto` switches to the continuous bar. See the Invite a Friend
// design review (#746).
const MAX_METER_PIPS = 12

export type InviteAllowanceTreatment = 'auto' | 'meter' | 'bar'

interface InviteAllowanceProps {
  /** Invites left this period. `0` → the exhausted (amber) state. */
  remaining: number
  /** Invites granted per period. Drives the meter's pip count and the bar's fill. */
  total: number
  /**
   * When the allowance refills, e.g. `"resets in 5 days"`. The caller computes this from
   * the rolling window (`periodStart + 7d − now`) and localizes it via `t()` — it is NOT
   * a fixed weekday, because the backend window is rolling, not calendar-aligned (spec D6).
   */
  resetLabel: string
  /** `auto` (default) shows the pip meter up to 12 invites, else the bar. Force `meter` or `bar` only for the catalog. */
  treatment?: InviteAllowanceTreatment
  className?: string
}

/**
 * The invite allowance a user is spending: `N invites left` with a `resetLabel`, over a
 * meter (a filled pip per invite) or a continuous bar for larger grants. Presentational
 * and declarative — as `remaining` drops the spent pip (or the bar) animates down on its
 * own, so no imperative "spend" trigger is needed. At `remaining === 0` the band turns
 * amber to pair with the exhausted composition.
 */
export function InviteAllowance({ remaining, total, resetLabel, treatment = 'auto', className }: InviteAllowanceProps) {
  const filled = Math.max(0, Math.min(remaining, total))
  const exhausted = filled <= 0
  const resolved = treatment === 'auto' ? (total <= MAX_METER_PIPS ? 'meter' : 'bar') : treatment

  return (
    <div
      className={cn(
        'rounded-[15px] border px-4 py-4 transition-colors',
        exhausted ? 'border-amber-hairline bg-amber-wash' : 'border-border bg-card-sunken',
        className
      )}>
      <div className="mb-3 flex items-baseline justify-between gap-2.5">
        <span
          className={cn(
            'whitespace-nowrap font-mono text-sm font-medium tracking-[-0.01em]',
            exhausted ? 'text-amber-strong' : 'text-foreground'
          )}>
          {exhausted ? (
            <b className="font-medium">0 left</b>
          ) : (
            <>
              <b className="font-medium">{filled}</b> {filled === 1 ? 'invite' : 'invites'} left
            </>
          )}
        </span>
        <span className="whitespace-nowrap text-xs text-muted-foreground">{resetLabel}</span>
      </div>

      {resolved === 'meter' ? (
        <div className="flex gap-1.5">
          {Array.from({ length: Math.max(0, total) }, (_, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: pips are positional and fixed-length
              key={i}
              className={cn(
                'h-[7px] flex-1 rounded-full transition-colors duration-300 motion-reduce:transition-none',
                i < filled ? 'bg-brand-green-600' : 'bg-track'
              )}
            />
          ))}
        </div>
      ) : (
        <div className="h-[7px] overflow-hidden rounded-full bg-track">
          <div
            className="h-full rounded-full bg-brand-green-600 transition-[width] duration-300 motion-reduce:transition-none"
            style={{ width: `${total > 0 ? (filled / total) * 100 : 0}%` }}
          />
        </div>
      )}
    </div>
  )
}
